import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

interface CashbackEarnedCardProps extends PressableAnalyticsProps {
  /** Title to be displayed on the card */
  titleText: string;
  /** Cashback amount to be displayed on the card*/
  cashbackBalanceText: string;
  /** Used to display the caption at the bottom of card */
  captionText: string;
  /** Determines if the card is in a selected state */
  isSelected?: boolean;
  /** Disable the card interaction */
  isDisabled?: boolean;
  /** Function used to handle the card press action */
  onCardPress: () => void;
}

const titleTextLineLimit = 1;
const amountTextLineLimit = 1;
const captionTextLineLimit = 1;

const CashbackEarnedCard = ({
  titleText,
  cashbackBalanceText,
  captionText,
  isSelected = false,
  isDisabled = false,
  onCardPress,
  analyticsId,
  customAttributes,
}: CashbackEarnedCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  return (
    <View style={styles.outerContainer}>
      <Pressable
        onPress={() => {
          cardPressAnalytics();
          onCardPress();
        }}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        role="button"
        accessible
        style={[
          styles.container,
          isFocused && styles.focus,
          isHovered && styles.hover,
          isPressed && styles.press,
          isSelected && styles.selected,
          isDisabled && styles.disabled,
        ]}
        testID="Cashback Earned Tile"
      >
        <View style={styles.mainContainer}>
          <View style={styles.textContainer}>
            <BodyBaseText
              style={[styles.title, isDisabled && styles.disabledText]}
              numberOfLines={titleTextLineLimit}
            >
              {titleText}
            </BodyBaseText>

            <TitleXLText
              style={[styles.cashbackText, isDisabled && styles.disabledText]}
              numberOfLines={amountTextLineLimit}
            >
              {cashbackBalanceText}
            </TitleXLText>

            <BodyXSText
              style={[styles.caption, isDisabled && styles.disabledText]}
              numberOfLines={captionTextLineLimit}
            >
              {captionText}
            </BodyXSText>
          </View>

          <View style={styles.iconContainer}>
            <Icon
              iconAccessibilityLabel="chevron-right-icon"
              iconKey="CHEVRON_RIGHT"
              iconSize="MD"
              iconColor={
                (isDisabled
                  ? theme.color.interactive.disabled.text
                  : theme.color.text.primary) as string
              }
            />
          </View>
        </View>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  caption: {
    color: theme.color.text.secondary,
    marginTop: theme.spacing.xs,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cashbackText: {
    marginTop: theme.spacing.s,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    borderRadius: 4,
    padding: theme.spacing.m,
  },
  disabled: {
    backgroundColor: theme.color.interactive.secondary,
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.expressive.varoPurple,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing.l,
  },
  mainContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  outerContainer: {
    backgroundColor: theme.color.background.tertiary,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
    borderColor: theme.color.border.primary,
  },
  selected: {
    borderColor: theme.color.expressive.varoPurple,
  },
  textContainer: {
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'space-between',
    minWidth: 0,
    paddingRight: theme.spacing.m,
  },
  title: {
    color: theme.color.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export default CashbackEarnedCard;
